import * as THREE from "three";
import { floorplannerStore } from "../../store/floorplannerStore";
import { editorStore } from "../../store/editorStore";
import { WallConnectionEnd, WallConnectionStart, WallType } from "../../types/floorplannerTypes";
import { updateAlignmentLines } from "./updateAligmentLines";
import { transformArcControlPoint } from "../../utils/arcUtils";

export type EdgePositionState = {
    start: THREE.Vector2;
    end: THREE.Vector2;
};

type AlignmentParams = {
    objectId: string;
    intendedPoint: THREE.Vector2;
    connectionEnd: typeof WallConnectionStart | typeof WallConnectionEnd;
    alignmentKey: "start" | "end";
    suppressSnapping: boolean;
    existingPositions: EdgePositionState | null;
    /** When angle snapping was applied, this is the snapped angle in degrees (0-360).
     * Used to filter alignment axes that are within 20° of the wall angle. */
    snappedWallAngle?: number;
};

/**
 * Check if X-alignment (vertical lines) should be applied given the wall angle.
 * Skip if wall is nearly vertical (within 20° of 90° or 270°).
 */
export const shouldApplyXAlignment = (snappedWallAngle: number | undefined): boolean => {
    if (snappedWallAngle === undefined) return true;
    // X-alignment aligns to vertical lines (90° or 270°)
    const diffFrom90 = Math.abs(snappedWallAngle - 90);
    const diffFrom270 = Math.abs(snappedWallAngle - 270);
    const minDiff = Math.min(diffFrom90, diffFrom270);
    return minDiff > 20;
};

/**
 * Check if Y-alignment (horizontal lines) should be applied given the wall angle.
 * Skip if wall is nearly horizontal (within 20° of 0°, 180°, or 360°).
 */
export const shouldApplyYAlignment = (snappedWallAngle: number | undefined): boolean => {
    if (snappedWallAngle === undefined) return true;
    // Y-alignment aligns to horizontal lines (0°, 180°, 360°)
    const diffFrom0 = Math.min(snappedWallAngle, 360 - snappedWallAngle);
    const diffFrom180 = Math.abs(snappedWallAngle - 180);
    const minDiff = Math.min(diffFrom0, diffFrom180);
    return minDiff > 20;
};

const getLatestPositions = (objectId: string): EdgePositionState | null => {
    const refreshed = floorplannerStore.findObjectId(objectId);
    if (!refreshed) {
        return null;
    }
    return {
        start: refreshed.start.clone(),
        end: refreshed.end.clone(),
    };
};

const clonePositions = (positions: EdgePositionState | null): EdgePositionState | null => {
    if (!positions) {
        return null;
    }
    return {
        start: positions.start.clone(),
        end: positions.end.clone(),
    };
};

export const applyAlignmentAfterSnap = (params: AlignmentParams): {
  applied: boolean;
  positions: EdgePositionState | null;
} => {
  const fallbackPositions = clonePositions(params.existingPositions);
  // Only skip alignment if Shift is pressed (manual override to disable all snapping)
  // Don't skip on suppressSnapping - that only controls connection operations, not alignment
  if (editorStore.isShiftPressed) {
        return { applied: false, positions: fallbackPositions };
    }

    const basePositions =
        fallbackPositions ??
        getLatestPositions(params.objectId);

    if (!basePositions) {
        return { applied: false, positions: fallbackPositions };
    }

  const movingKey: "start" | "end" = params.alignmentKey;
  const overrides: EdgePositionState = {
    start: basePositions.start.clone(),
    end: basePositions.end.clone(),
  };
  overrides[movingKey] = params.intendedPoint.clone();

    // Check if we should use edge-based alignment (for walls when ALT is not pressed)
    const object = floorplannerStore.findObjectId(params.objectId);
    const shouldUseEdgePoints = object?.type === 'wall' && !editorStore.isAltPressed;

    if (shouldUseEdgePoints && object?.type === 'wall') {
        // Edge-based alignment: Check both edge points at the dragging end.
        // For arc walls, the wall's visual corner at the moving endpoint is
        // offset along the Bezier tangent's perpendicular, not the chord's.
        // Using the chord here would collapse the two corners onto the chord
        // line for curved walls (looking like center-based snapping).
        const wallType = object as WallType;
        const isArcWall = wallType.lineForm === "arc" && !!wallType.controlPoint;

        let tangent: THREE.Vector2;
        if (isArcWall && wallType.controlPoint) {
            // Reproject the control point for the intended endpoint position so
            // we evaluate the tangent at the POST-drag endpoint, matching how
            // transformArcControlPoint is applied when the drag commits.
            const projectedControlPoint = transformArcControlPoint(
                wallType.controlPoint,
                wallType.start,
                wallType.end,
                overrides.start,
                overrides.end,
            );
            tangent = movingKey === "start"
                ? new THREE.Vector2().subVectors(projectedControlPoint, overrides.start)
                : new THREE.Vector2().subVectors(overrides.end, projectedControlPoint);
            if (tangent.lengthSq() < 1e-12) {
                tangent = new THREE.Vector2(
                    overrides.end.x - overrides.start.x,
                    overrides.end.y - overrides.start.y,
                );
            }
        } else {
            tangent = new THREE.Vector2(
                overrides.end.x - overrides.start.x,
                overrides.end.y - overrides.start.y,
            );
        }
        if (tangent.lengthSq() > 1e-12) {
            tangent.normalize();
        } else {
            tangent.set(1, 0);
        }

        const perpendicular = new THREE.Vector2(-tangent.y, tangent.x);
        const width = object.wallWidth || floorplannerStore.wallWidth;
        const halfWidth = width / 2;

        // Calculate the two edge points at the dragging end
        const movingPoint = overrides[movingKey];
        const leftEdge = new THREE.Vector2(
            movingPoint.x + perpendicular.x * halfWidth,
            movingPoint.y + perpendicular.y * halfWidth
        );
        const rightEdge = new THREE.Vector2(
            movingPoint.x - perpendicular.x * halfWidth,
            movingPoint.y - perpendicular.y * halfWidth
        );

        const edgePoints = [
            { point: leftEdge, label: 'left' },
            { point: rightEdge, label: 'right' }
        ];

        type EdgeSnapResult = {
            snapDelta: THREE.Vector2;
            axes: number;
            distance: number;
            targetX?: number;
            targetY?: number;
        };

        let bestSnap: EdgeSnapResult | null = null;

        // Check each edge point for alignment
        for (const edge of edgePoints) {
            const preview = floorplannerStore.previewAlignmentSnap(
                params.objectId,
                params.connectionEnd,
                edge.point,
                overrides
            );

            // Filter alignment axes based on wall angle (skip axes within 20° of wall direction)
            const applyX = preview.snappedX && shouldApplyXAlignment(params.snappedWallAngle);
            const applyY = preview.snappedY && shouldApplyYAlignment(params.snappedWallAngle);
            const axes = (applyX ? 1 : 0) + (applyY ? 1 : 0);

            if (preview.snapped && axes > 0) {
                // Calculate snap delta based on alignment line positions (only for allowed axes)
                const snapDelta = new THREE.Vector2(
                    (applyX && preview.targetX !== undefined ? (preview.targetX - edge.point.x) : 0),
                    (applyY && preview.targetY !== undefined ? (preview.targetY - edge.point.y) : 0)
                );
                const distance = snapDelta.length();

                // Pick best snap: prioritize more axes, then smaller distance
                if (!bestSnap || axes > bestSnap.axes || (axes === bestSnap.axes && distance < bestSnap.distance)) {
                    bestSnap = {
                        snapDelta,
                        axes,
                        distance,
                        targetX: preview.targetX,
                        targetY: preview.targetY
                    };
                }
            }
        }

        if (bestSnap) {
            // Apply snap delta to the moving endpoint
            const nextPoint = basePositions[movingKey].clone().add(bestSnap.snapDelta);

            // During drag, always apply snap if found (no tolerance check needed)
            // This ensures deterministic snapping for the same mouse position
            const finalPositions: EdgePositionState = {
                start: basePositions.start.clone(),
                end: basePositions.end.clone(),
            };
            finalPositions[movingKey].copy(nextPoint);

            const finalPoint = movingKey === "start" ? finalPositions.start : finalPositions.end;
            updateAlignmentLines(params.objectId, finalPoint.x, finalPoint.y, movingKey);

            return {
                applied: true,
                positions: finalPositions,
            };
        }

        // No edge-based alignment found
        return { applied: false, positions: clonePositions(basePositions) };
    }

    // Center-based alignment (legacy mode for non-walls or when ALT is pressed)
  const preview = floorplannerStore.previewAlignmentSnap(
    params.objectId,
    params.connectionEnd,
    params.intendedPoint.clone(),
    overrides,
  );

    // Filter alignment axes based on wall angle (skip axes within 20° of wall direction)
    const applyX = preview.snappedX && shouldApplyXAlignment(params.snappedWallAngle);
    const applyY = preview.snappedY && shouldApplyYAlignment(params.snappedWallAngle);

    if (!preview.snapped || (!applyX && !applyY)) {
        return { applied: false, positions: clonePositions(basePositions) };
    }

    const previewPoint = movingKey === "start" ? preview.start : preview.end;
    const nextPoint = basePositions[movingKey].clone();

    if (applyX) {
        nextPoint.x = previewPoint.x;
    }
    if (applyY) {
        nextPoint.y = previewPoint.y;
    }

  const finalPositions: EdgePositionState = {
    start: basePositions.start.clone(),
    end: basePositions.end.clone(),
  };
  finalPositions[movingKey].copy(nextPoint);

  const finalPoint = movingKey === "start" ? finalPositions.start : finalPositions.end;
    updateAlignmentLines(params.objectId, finalPoint.x, finalPoint.y, movingKey);

    return {
        applied: true,
        positions: finalPositions,
    };
};
